import React, { useState, useEffect } from 'react';
import { Download, FileText, Lock, CheckCircle, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { trackConversion } from '../utils/analytics';
import EmailCourseSignup from '../components/EmailCourseSignup';
import CertificateGenerator from '../components/CertificateGenerator';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Resources = () => {
  const [email, setEmail] = useState('');
  const [selectedResource, setSelectedResource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resources, setResources] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API}/admin/resources/items?published_only=true`);
      setResources(response.data);
    } catch (error) {
      console.error('Error fetching resources:', error);
    }
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    if (!email || !selectedResource) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/resources/request`, {
        email,
        resource_id: selectedResource.id.toString(),
        resource_title: selectedResource.title
      });
      toast({
        title: "Access Granted!",
        description: response.data.message || "Check your email for the download link.",
      });
      
      // Track conversion
      trackConversion('resource_download', { 
        resource_id: selectedResource.id,
        resource_title: selectedResource.title 
      });
      
      setEmail('');
      setDialogOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const openDownloadDialog = (resource) => {
    setSelectedResource(resource);
    setDialogOpen(true);
  };

  const getFileIcon = (type) => {
    return <FileText className="h-5 w-5" />;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-2 mb-6">
              <Lock className="h-4 w-4 text-orange-400" />
              <span className="text-sm font-medium text-orange-400">Free Resources - Email Required</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Free Resources for <span className="text-gradient">Drivers and Fleets</span>
            </h1>
            <p className="text-xl text-gray-300">
              Access our growing library of downloadable resources designed to help reduce accidents 
              and protect your operation.
            </p>
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="section-padding bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Info Banner */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 mb-8">
              <div className="flex items-start space-x-3">
                <Mail className="h-6 w-6 text-orange-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-white font-semibold mb-1">Email Signup Required</h3>
                  <p className="text-gray-400 text-sm">
                    To access our downloadable resources, simply enter your email. We'll send you instant access 
                    and keep you updated with new resources and safety content.
                  </p>
                </div>
              </div>
            </div>

            {/* Available Resources */}
            <h2 className="text-2xl font-bold text-white mb-6">Available Resources</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {resources.map((resource) => (
                <Card key={resource.id} className="bg-gray-800 border-gray-700 card-hover">
                  <CardHeader>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/10">
                        {getFileIcon(resource.type)}
                        <span className="ml-1 text-orange-400">{resource.type}</span>
                      </div>
                      <span className="text-xs text-gray-400">{resource.fileSize}</span>
                    </div>
                    <CardTitle className="text-white text-xl">{resource.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-400 mb-4">
                      {resource.description}
                    </CardDescription>
                    <Dialog open={dialogOpen && selectedResource?.id === resource.id} onOpenChange={(open) => {
                      setDialogOpen(open);
                      if (!open) setSelectedResource(null);
                    }}>
                      <DialogTrigger asChild>
                        <Button 
                          className="w-full btn-primary text-white font-semibold"
                          onClick={() => openDownloadDialog(resource)}
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Download Resource
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-gray-800 border-gray-700 text-white">
                        <DialogHeader>
                          <DialogTitle className="text-white">Access Resource</DialogTitle>
                          <DialogDescription className="text-gray-400">
                            Enter your email to receive instant access to <strong>{selectedResource?.title}</strong>
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleDownload} className="space-y-4">
                          <div className="space-y-2">
                            <label htmlFor="resource-email" className="text-sm font-medium text-gray-200">
                              Email Address
                            </label>
                            <Input
                              id="resource-email"
                              type="email"
                              placeholder="your.email@example.com"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
                              required
                            />
                          </div>
                          <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                            <div className="flex items-start space-x-2">
                              <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                              <p className="text-sm text-gray-300">
                                We respect your privacy. Your email will only be used for resource delivery and 
                                optional safety updates. Unsubscribe anytime.
                              </p>
                            </div>
                          </div>
                          <Button
                            type="submit"
                            className="w-full btn-primary text-white font-semibold h-11"
                            disabled={loading}
                          >
                            {loading ? 'Processing...' : 'Get Instant Access'}
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="section-padding bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Email Course Signup */}
            <div className="mb-16">
              <EmailCourseSignup />
            </div>

            {/* Certificate Generator */}
            <div className="mb-16">
              <CertificateGenerator />
            </div>

            <h2 className="text-3xl font-bold text-white mb-8 text-center">What You'll Get</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  title: 'Instant Access',
                  description: 'Download resources immediately after email verification.'
                },
                {
                  title: 'Regular Updates',
                  description: 'Be notified when new resources and guides are published.'
                },
                {
                  title: 'Practical Tools',
                  description: 'Checklists, templates, and guides you can use right away.'
                },
                {
                  title: 'No Spam Promise',
                  description: 'We value your inbox. Unsubscribe anytime with one click.'
                }
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-3 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <CheckCircle className="h-6 w-6 text-orange-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                    <p className="text-gray-400 text-sm">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
